import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { createSolutionArea } from "./api.js";

/**
 * Create-a-solution-area modal.
 *
 * The Solution axis (XOP's go-to-market pillars — Unified Communications /
 * Public Safety / Financial Services / …) used to be a hard-coded enum;
 * it is now a managed list. This dialog is the "add a pillar" affordance,
 * mounted from the dedicated `SolutionAreasManagePage`. Mirrors
 * `NewCategoryModal` (the Rails Sluggable concern derives the slug from the
 * name); the caller owns cache invalidation through `onCreated`.
 */
export function NewSolutionAreaModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [visible, setVisible] = useState(true);
  // Off by default, matching the server. Unlike the customer flag (which
  // defaults true for pillars), reseller exposure is always a deliberate act.
  const [resellerVisible, setResellerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createSolutionArea({
        solution_area: {
          name: name.trim(),
          visible_in_customer_portal: visible,
          visible_in_reseller_portal: resellerVisible,
        },
      }),
    onSuccess: () => {
      setName("");
      setVisible(true);
      setError(null);
      onCreated();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not create solution area");
    },
  });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
          New solution area
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 12, fontWeight: 400, color: "text.disabled" }}>
          A product-line pillar documents can be tagged with (a doc can carry several).
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "grid", gap: 2, pt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mutation.mutate();
          }}
        >
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <TextField
              id="new-solution-area-name"
              label="Name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Healthcare"
              inputProps={{ required: true }}
              fullWidth
            />
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              The slug auto-derives from the name (lowercase, dashes).
            </Typography>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in customer portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                When off, the pillar is hidden from customer-facing surfaces. You
                can flip this any time from the pencil edit.
              </Box>
            </Box>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={resellerVisible}
              onChange={(e) => setResellerVisible(e.target.checked)}
              data-testid="new-solution-area-visible-resellers"
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in reseller portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                Independent of the customer switch. When on, this pillar
                appears as a filter chip for XOP Reseller partners.
              </Box>
            </Box>
          </Box>

          {error && (
            <Box
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
                bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              mx: -3,
              mb: -2,
              px: 3,
              py: 1.5,
            }}
          >
            <Button type="button" variant="text" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Creating…" : "Create solution area"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
